import React, { useState, useMemo, useEffect, useRef } from 'react';
import './Sidebar.css';
import FEATURE_PAGES from '../features/registry';
import ClientBrand from './ClientBrand';

const eligibilityIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 12l2 2 4-4" />
    <circle cx="12" cy="12" r="10" />
  </svg>
);

const claimsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
    <path d="M10 9H8" />
  </svg>
);

const settingsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const remittancesIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
    <path d="M6 15h4" />
    <path d="M14 15h4" />
  </svg>
);

const dashboardIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="12" width="4" height="9" rx="1" />
    <rect x="10" y="7" width="4" height="14" rx="1" />
    <rect x="17" y="3" width="4" height="18" rx="1" />
  </svg>
);

const usersIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const appointmentsIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const arIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 1v22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const backIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5" />
    <polyline points="12,19 5,12 12,5" />
  </svg>
);

const searchIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

function PracticeSearchSelector({ practices, selectedPractice, onPracticeChange }) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const trimmedQuery = query.trim();
  const filtered = useMemo(() => {
    const q = trimmedQuery.toLowerCase();
    if (!q) return [];
    return practices.filter((p) => (p.name || '').toLowerCase().includes(q));
  }, [trimmedQuery, practices]);

  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen]);

  const handleSelect = (p) => {
    if (p) onPracticeChange(p);
    setIsOpen(false);
    setQuery('');
    inputRef.current?.blur();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filtered.length > 0) setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (filtered.length > 0) setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) handleSelect(filtered[activeIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
      inputRef.current?.blur();
    }
  };

  const displayValue = isOpen ? query : (selectedPractice ? selectedPractice.name : '');

  return (
    <div className="practice-search-selector" ref={containerRef}>
      <div className={`practice-search-field${isOpen ? ' open' : ''}`}>
        <span className="practice-search-field-icon">{searchIcon}</span>
        <input
          ref={inputRef}
          type="text"
          className="practice-search-field-input"
          placeholder={selectedPractice ? selectedPractice.name : 'Search practices'}
          value={displayValue}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => { setIsOpen(true); setQuery(''); }}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </div>
      {isOpen && trimmedQuery && (
        <div className="practice-search-field-results">
          {filtered.length === 0 ? (
            <div className="practice-search-field-empty">No matches</div>
          ) : (
            filtered.map((p, idx) => (
              <div
                key={p.id}
                className={
                  'practice-search-field-result' +
                  (idx === activeIndex ? ' active' : '') +
                  (selectedPractice && p.id === selectedPractice.id ? ' current' : '')
                }
                onClick={() => handleSelect(p)}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                {p.name}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const ROLE_LABELS = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  biller: 'Biller',
  client: 'Client',
};

// Fixed pages visible to client role (not configurable per user), in the order the
// client nav shows them (David, 2026-10-21). The client nav is FLAT — no groups, no
// disclosure — because seven items need no grouping and a client never sees the rest.
// Payments is left off for now: most payers have no ERA connection, so the page
// would be mostly empty for a clinic (David, 2026-10-21). Staff still see it.
// App.js reads this list too, to land on the dashboard when "View as client" turns on
// from a staff page, or when a client deep-links to a page not on it.
// 'lists' is a claim list's page (/p/<id>/lists/<id>), reached from the Claims page; it
// has no nav item of its own, so it is here only to let a client open a list's link.
export const CLIENT_PAGES = ['dashboard', 'appointments', 'eligibility', 'claims', 'patients', 'ar', 'practice-details', 'lists'];

// The four groups, in a fixed order. They set the ORDER of the staff nav, which is drawn
// flat with no headings: the Work pages first, one line under them, then the rest
// (David, 2026-10-25). A page id nobody has placed lands in Work rather than
// disappearing, so a new page from another strand is visible the day it ships.
const NAV_GROUPS = [
  {
    key: 'work',
    label: 'Work',
    ids: ['appointments', 'claims', 'queue', 'worklist', 'tasks', 'mail', 'charges', 'collections', 'patient-balances', 'claim-editor'],
  },
  // Dashboard opens the section under the line (David, 2026-10-25).
  { key: 'patients', label: 'Patients', ids: ['dashboard', 'patients', 'eligibility', 'authorizations', 'statements'] },
  { key: 'money', label: 'Money', ids: ['payments', 'ar', 'waterfall', 'success', 'reports'] },
  { key: 'setup', label: 'Setup', ids: ['practice-details', 'data', 'pms-coverage', 'fee-schedule', 'integrations', 'users'] },
];

// claim-editor (David, 2026-10-25): opened from a claim's Edit on Claims, Charges or the
// submit queue, never browsed to; the page itself still renders when one names it.
// authorizations (David, 2026-10-25): parked until there is time to work out what it is for.
const HIDDEN_FROM_NAV = new Set(['reports', 'pms-coverage', 'patient-balances', 'statements', 'fee-schedule', 'collections', 'data', 'claim-editor', 'authorizations']);

const GROUP_FOR = {};
NAV_GROUPS.forEach(({ key, ids }) => ids.forEach((id) => { GROUP_FOR[id] = key; }));

export function groupItems(items) {
  const byKey = new Map(NAV_GROUPS.map(({ key, label }) => [key, { key, label, items: [] }]));
  items.forEach((item) => {
    const group = byKey.get(GROUP_FOR[item.id] || 'work');
    group.items.push(item);
  });
  // Within a group the order is the group's own list, then anything unplaced.
  NAV_GROUPS.forEach(({ key, ids }) => {
    const group = byKey.get(key);
    group.items.sort((a, b) => {
      const ai = ids.indexOf(a.id);
      const bi = ids.indexOf(b.id);
      return (ai === -1 ? ids.length : ai) - (bi === -1 ? ids.length : bi);
    });
  });
  return [...byKey.values()].filter((group) => group.items.length > 0);
}

// One nav entry. Shared by the staff nav and the client nav so the two
// never drift in markup or styling.
function NavItem({ item, activePage, onPageChange }) {
  return (
    <button
      className={`sidebar-item ${activePage === item.id ? 'active' : ''} ${item.disabled ? 'disabled' : ''}`}
      onClick={() => !item.disabled && onPageChange(item.id)}
      disabled={item.disabled}
    >
      <span className="sidebar-icon">{item.icon}</span>
      <span className="sidebar-label">{item.label}</span>
      {item.disabled && <span className="coming-soon">Soon</span>}
    </button>
  );
}

const tasksIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 11 12 14 22 4" />
    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
  </svg>
);

// A simple gauge: the page leads with a rate.
const successIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 18a8 8 0 1 1 16 0" />
    <line x1="12" y1="18" x2="16" y2="11" />
  </svg>
);

const mailIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <polyline points="3 7 12 13 21 7" />
  </svg>
);

const waterfallIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 20h18" />
    <rect x="4" y="10" width="3" height="10" />
    <rect x="9" y="7" width="3" height="5" />
    <rect x="14" y="4" width="3" height="4" />
    <path d="M2 3h20" strokeDasharray="2 2" />
  </svg>
);

const reviewIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M9 4v16" />
  </svg>
);

const eyeIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

function Sidebar({ activePage, onPageChange, user, onLogout, practices, selectedPractice, onPracticeChange, onBackToPractices, features, showAppointments, viewAsClient, onToggleViewAsClient }) {
  // "View as client" (super_admin only): the nav, the practice picker and the footer
  // all read the EFFECTIVE role, so a super_admin previewing sees exactly what a client
  // sees. Only the role badge and the toggle itself read the real role.
  const realSuperAdmin = !!(user && user.role === 'super_admin');
  // The preview needs an open practice. With none selected (Admin, Mission Control,
  // Rules, Onboarding, the Practices landing page) the toggle is not offered and a
  // persisted flag is treated as off, so nobody is stranded with a client nav.
  const previewOn = !!(viewAsClient && realSuperAdmin && selectedPractice);
  const effectiveRole = previewOn ? 'client' : user && user.role;
  const clientView = effectiveRole === 'client';

  const isAdmin = user && (effectiveRole === 'admin' || effectiveRole === 'super_admin');

  const isSuperAdmin = user && effectiveRole === 'super_admin';

  // Build menu items, filtering by role + page access
  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: dashboardIcon },
    // Shown for any practice with rows in the appointments table, whichever feed
    // wrote them (App.js reads the signal off the capabilities fetch).
    { id: 'appointments', label: 'Appointments', icon: appointmentsIcon, adminOnly: true, requiresAppointments: true },
    { id: 'eligibility', label: 'Eligibility', icon: eligibilityIcon },
    { id: 'claims', label: 'Claims', icon: claimsIcon },
    { id: 'payments', label: 'Payments', icon: remittancesIcon },
    { id: 'ar', label: 'A/R', icon: arIcon },
    // David's audit view of the practice's collections: the gap to 98% as blocks.
    { id: 'waterfall', label: 'Waterfall to 98%', icon: waterfallIcon },
    // The practice's client tasks (docs/slack-pane.md), the Slack Tasks tab's page. Staff
    // only: every staff role, and off the client list (CLIENT_PAGES).
    { id: 'tasks', label: 'Tasks', icon: tasksIcon },
    // The weekly report's numbers for the practice (docs/slack-pane.md, "The page"):
    // the Slack tab's bookmark. Staff only; CLIENT_PAGES keeps it off the client nav.
    { id: 'success', label: 'Customer Success', icon: successIcon },
    // The practice's mail threads (docs/slack-pane.md), the Slack Mail tab's page. Staff
    // only: every staff role, and off the client list (CLIENT_PAGES).
    { id: 'mail', label: 'Mail', icon: mailIcon },
    // NB Mission Control is deliberately NOT here, and has no footer button either
    // (hidden 2026-10-25); /mission-control still opens it for a super_admin.
    // A client calls their practice a clinic; staff keep "Practice Details".
    { id: 'practice-details', label: 'Practice Details', clientLabel: 'Clinic Details', icon: settingsIcon },
    // Gated pages from the feature registry (features/registry.js). They are filtered
    // out below unless the practice's capabilities turn them on, so a practice whose
    // source supplies no claim statuses never sees a denial worklist it cannot fill.
    ...FEATURE_PAGES.map(({ id, label, icon, adminOnly, feature }) => ({
      id,
      label,
      icon,
      adminOnly,
      feature,
    })),
  ];

  // Remittances is only meaningful when 835 ERAs flow into Atlas through
  // a directly-integrated clearinghouse (Stedi or ClaimMD). Practices with
  // `clearinghouse='other'` route through a third-party we don't ingest
  // ERAs from, so they get nothing useful on this page either. Hide for
  // everyone — admins included — when the practice doesn't use one of our
  // integrated clearinghouses.
  const INTEGRATED_CLEARINGHOUSES = ['stedi', 'claimmd'];
  const hasClearinghouse = !!(
    selectedPractice && INTEGRATED_CLEARINGHOUSES.includes(selectedPractice.clearinghouse)
  );

  // Determine which pages the user can see
  let visibleItems = allMenuItems.filter(
    item => !item.requiresClearinghouse || hasClearinghouse
  );
  // Appointments: staff see it only for a practice with appointment rows; a client
  // sees it always (the page shows its empty state when there is nothing yet).
  visibleItems = visibleItems.filter(item => !item.requiresAppointments || showAppointments || clientView);

  // The capability gate. An item carrying a `feature` shows only when that feature is
  // on for the selected practice. `features` is empty while it loads and when no
  // practice is open, so these entries stay hidden until the answer is known — fail
  // closed, the same way the backend does. Ungated items are unaffected.
  const featureMap = features || {};
  visibleItems = visibleItems.filter(item => !item.feature || featureMap[item.feature]);

  // Off the nav for every role, still reachable by URL (David, 2026-10-25). Reports and
  // PMS coverage are clutter for a biller; Patient balances and Statements are both
  // covered by A/R's Patient tab; Fee Schedule is a tab on Practice Details; Collections
  // is patient outreach, which Atlas is not doing (its Refund due list is on A/R).
  // Data is a table under Integrations on Practice Details.
  visibleItems = visibleItems.filter(item => !HIDDEN_FROM_NAV.has(item.id));

  if (!isAdmin && user) {
    if (clientView) {
      // Clients get a fixed page set — not configurable per user — in CLIENT_PAGES
      // order, flat. adminOnly does not apply: Appointments is on the client list.
      visibleItems = CLIENT_PAGES.map((id) => visibleItems.find((item) => item.id === id))
        .filter(Boolean)
        .map((item) => (item.clientLabel ? { ...item, label: item.clientLabel } : item));
    } else {
      // Billers see all pages for their assigned practices (except admin-only)
      visibleItems = visibleItems.filter(item => !item.adminOnly);
    }
  }

  const groups = clientView ? [] : groupItems(visibleItems);

  // A client with one clinic sees that clinic's logo and name top left, in place of the
  // Atlas logo and the practice picker. A client with several keeps both. A super_admin
  // previewing is looking at one practice, whatever their own list holds.
  const singleClinicBrand = !!(
    clientView && selectedPractice && (previewOn || !practices || practices.length <= 1)
  );

  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        {singleClinicBrand ? (
          <ClientBrand practice={selectedPractice} />
        ) : (
          <img src="/logo.png" alt="Atlas Billers" className="sidebar-logo" />
        )}
      </div>

      {/* Practice Selector — staff, and clients with several clinics */}
      {!singleClinicBrand && practices && practices.length > 0 && (isAdmin || practices.length > 1) && (
        <div className="practice-selector">
          <label className="practice-label">Practice</label>
          {isAdmin ? (
            <PracticeSearchSelector
              practices={practices}
              selectedPractice={selectedPractice}
              onPracticeChange={onPracticeChange}
            />
          ) : (
            <select
              className="practice-dropdown"
              value={selectedPractice ? selectedPractice.id : ''}
              onChange={(e) => {
                const practice = practices.find(p => p.id === parseInt(e.target.value));
                if (practice) onPracticeChange(practice);
              }}
            >
              {!selectedPractice && <option value="">Select a practice</option>}
              {practices.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          {isAdmin && onBackToPractices && (
            <button className="back-to-practices" onClick={onBackToPractices}>
              {backIcon}
              <span>All Practices</span>
            </button>
          )}
        </div>
      )}

      {/* Client practice label — shown when dropdown is hidden and the brand is not */}
      {!singleClinicBrand && selectedPractice && !(isAdmin || practices.length > 1) && (
        <div className="client-practice-label">Client: {selectedPractice.name}</div>
      )}

      <nav className="sidebar-nav">
        {clientView
          ? visibleItems.map((item) => (
              <NavItem key={item.id} item={item} activePage={activePage} onPageChange={onPageChange} />
            ))
          : groups.map(({ key, items }, i) => (
              <React.Fragment key={key}>
                {items.map((item) => (
                  <NavItem key={item.id} item={item} activePage={activePage} onPageChange={onPageChange} />
                ))}
                {key === 'work' && i < groups.length - 1 && (
                  <hr className="sidebar-divider" data-testid="nav-work-divider" />
                )}
              </React.Fragment>
            ))}
      </nav>

      <div className="sidebar-footer">
        {user && (
          <div className="user-section">
            <div className="user-info">
              <span className="user-email">{user.name || user.email}</span>
              {/* The real role, or while previewing only the preview badge, so the
                  footer reads as the client's would. */}
              {previewOn ? (
                <span className="view-as-client-badge">Viewing as client</span>
              ) : (
                <span className="role-badge" data-role={user.role}>{ROLE_LABELS[user.role] || user.role}</span>
              )}
            </div>
            {/* The preview switch: super_admin only (real role), only with a practice
                open, and the one footer button that survives the client view, since it
                is the way back. */}
            {realSuperAdmin && selectedPractice && onToggleViewAsClient && (
              <button
                type="button"
                className={`logout-button view-as-client-toggle ${previewOn ? 'active' : ''}`}
                onClick={onToggleViewAsClient}
              >
                {previewOn ? backIcon : eyeIcon}
                {previewOn ? 'Back to Atlas view' : 'View as client'}
              </button>
            )}
            {isSuperAdmin && (
              <button className="logout-button" onClick={() => onPageChange('admin')}>
                {usersIcon}
                Admin
              </button>
            )}
            {/* Rules engine admin — admin-level (billing experts author + review
                rules; backend enforces per-endpoint), with activation and
                practice-mode changes held at super_admin inside the page.
                Cross-practice footer placement matches Mission Control. Billers
                meet rules only as ledger chips + receipts, never this editor. */}
            {isAdmin && (
              <button
                className={`logout-button ${activePage === 'rules' ? 'active' : ''}`}
                onClick={() => onPageChange('rules')}
              >
                {settingsIcon}
                Rules
              </button>
            )}
            <button className="logout-button" onClick={onLogout}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16,17 21,12 16,7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Sign Out
            </button>
          </div>
        )}
        <span className="version">v1.2.0</span>
      </div>
    </aside>
  );
}

export default Sidebar;
